import { useState, useEffect } from "react";
import MiniLoadingSpinner from "../../components/MiniLoadingSpinner";
import CloseButton from "../../components/CloseButton";
import SimpleTable from "../../components/SimpleTable";
import { AccessDetailsData, accessHistoryTableColumns } from "../functions";
import type { TFunction } from "i18next";
import { useDarkMode } from "../../../../context/ThemeContext";
import { fetchAccessHistory } from "../../../../store/oasis/accessStore";

interface AccessHistoryItem {
  page: any[];
  lastDocId: string;
  totalPages: number;
}

interface ModalAccessHistoryProps {
  data: any;
  isLoadingUser: boolean;
  showDetails: boolean;
  actiocCloseModal: () => void;
  t: TFunction<"translation", undefined>;
  accessHistory: AccessHistoryItem[];
  setAccessHistory: React.Dispatch<React.SetStateAction<AccessHistoryItem[]>>;
}

const ModalAccessHistory: React.FC<ModalAccessHistoryProps> = ({
  data,
  isLoadingUser,
  showDetails,
  actiocCloseModal,
  t,
  accessHistory,
  setAccessHistory,
}) => {
  // Modo oscuro
  const { isDarkMode } = useDarkMode();

  // Estados locales
  const [pagination, setPagination] = useState<number>(1);
  const [previusLastDocID, setPreviusLastDocID] = useState<string | null>(null);
  const [currentLastDocID, setCurrentLastDocID] = useState<string>("");

  // FUNCIONES

  const itemsPerPage = 6; // o el valor que uses

  const fetchPage = async (pageNumber: number, lastDocId: string) => {
    if (!data?.id || !data?.yardId) return;
    try {
      const historyData = await fetchAccessHistory({
        yardId: data.yardId,
        accessId: data.id,
        itemsPerPage,
        lastDocId,
      });
      setAccessHistory((prev) => {
        const updated = [...prev];
        updated[pageNumber - 1] = historyData;
        return updated;
      });
    } catch (error) {
      // Manejo de errores
    }
  };

  const nextPage = async () => {
    const nextPageNumber = pagination + 1;
    // Si no existe la página, la traemos antes de cambiar el estado
    if (!accessHistory[nextPageNumber - 1]) {
      await fetchPage(nextPageNumber, currentLastDocID);
    }
    setPreviusLastDocID(currentLastDocID);
    setPagination(nextPageNumber);
  };

  const previusPage = async () => {
    const prevPageNumber = pagination - 1;
    // Si no existe la página, la traemos antes de cambiar el estado
    if (!accessHistory[prevPageNumber - 1]) {
      await fetchPage(
        prevPageNumber,
        accessHistory[prevPageNumber - 2]?.lastDocId ?? ""
      );
    }
    setPagination(prevPageNumber);
    setCurrentLastDocID(previusLastDocID ?? "");
    setPreviusLastDocID(accessHistory[prevPageNumber - 2]?.lastDocId ?? null);
  };

  useEffect(() => {
    setPreviusLastDocID(accessHistory[pagination - 2]?.lastDocId ?? null);
    setCurrentLastDocID(accessHistory[pagination - 1]?.lastDocId ?? "");
  }, [accessHistory, pagination]);

  return (
    <div className="fixed flex flex-col gap-3 w-full h-screen bottom-0 right-0 bg-[#000000d9] backdrop-blur-sm z-10 px-[15%] pt-2 pb-5">
      <div
        className={`w-full flex flex-col rounded-2xl gap-3 px-8 pt-5 ${
          isDarkMode ? "bg-[#232323] text-white" : "bg-white"
        }`}
      >
        {showDetails && (
          <div className="flex flex-col">
            <div className="flex justify-center gap-0">
              <div className="w-full">
                <h1 className="font-bold text-[#1C93FF] text-xl text-center pl-20">
                  {t("accessDetails")}
                </h1>
              </div>

              <div className="flex gap-5 justify-end items-center w-28">
                {isLoadingUser && <MiniLoadingSpinner />}

                <CloseButton
                  handleClick={() => actiocCloseModal()}
                  text={t("close")}
                />
              </div>
            </div>

            <div
              className={`p-3 px-10 grid grid-cols-${
                AccessDetailsData(data, t).columns.length
              } gap-5 bg-black/5 rounded-md mb-1 ${
                isDarkMode ? "bg-dark-surface text-white " : "text-gray-900"
              }`}
            >
              {AccessDetailsData(data, t).columns.map(
                (column: any, index: number) => (
                  <div className="flex flex-col gap-1 " key={index}>
                    <h1 className="font-bold text-base text-[#0052A2]">
                      {column.title}
                    </h1>
                    <hr />
                    <div className="flex flex-col text-sm font-Outfit">
                      {column.data.map((data: any, idx: number) => (
                        <p key={idx}>{`${data.label}: ${data.value}`}</p>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )}
        <div className="flex flex-col gap-2 overflow-auto">
          <h2 className="font-bold text-[#1C93FF] text-xl text-center">
            {t("accessHistory")}
          </h2>
          <div className="flex w-full px-2 pb-5 overflow-auto">
            <SimpleTable
              key={Array.isArray(accessHistory) ? accessHistory.length : 0}
              data={
                Array.isArray(accessHistory) &&
                accessHistory[pagination - 1]?.page
                  ? accessHistory[pagination - 1].page
                  : []
              }
              tableColumns={accessHistoryTableColumns}
              totalPages={
                Array.isArray(accessHistory) && accessHistory[0]?.totalPages
                  ? accessHistory[0].totalPages
                  : 1
              }
              pagination={pagination}
              nextPage={nextPage}
              previusPage={previusPage}
              pageRenderingType="api"
              t={t}
              defaultText={
                isLoadingUser ? t("loadingAccessHistory") : t("NoAccessHistory")
              }
              action1={data.itemSelected}
              disabled={
                Array.isArray(accessHistory) &&
                accessHistory.length !== (accessHistory[0]?.totalPages ?? 1) &&
                isLoadingUser
              }
              isDarkMode={isDarkMode}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalAccessHistory;
